import React, { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Upload, Trash2, Image as ImageIcon, X, FileImage, Plus, Package, Download, Camera,
  FileText, PackageCheck, Info, LayoutGrid, ListTree,
} from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { usePermission } from "@/hooks/usePermission";

const CLAS_COLOR = {
  B: "bg-[hsl(173_80%_40%/0.18)] text-primary border-primary/40",
  C: "bg-[hsl(199_88%_48%/0.18)] text-[hsl(var(--accent))] border-[hsl(var(--accent)/0.4)]",
  D: "bg-[hsl(38_92%_55%/0.18)] text-[hsl(var(--status-pending))] border-[hsl(var(--status-pending)/0.4)]",
};

const TIPO_EV_LABEL = {
  llegada: "Llegada",
  contenedor_limpio: "Contenedor limpio",
};

const TIPO_EV_BADGE = {
  llegada: "bg-primary/15 text-primary border-primary/40",
  contenedor_limpio: "bg-[hsl(142_71%_45%/0.18)] text-[hsl(142_71%_60%)] border-[hsl(142_71%_45%/0.4)]",
};

function fileSrc(otId, evId) {
  const token = localStorage.getItem("ot_token");
  return `${API}/ots/${otId}/evidencias/${evId}/file?t=${encodeURIComponent(token || "")}`;
}

function normalizePuerto(str) {
  return (str || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export default function EvidenciasTab({ otId, folio, items: otItems = [], puertoEntrada = "" }) {
  const qc = useQueryClient();
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const canUpload = usePermission("evidencia_upload");
  const canEdit = usePermission("evidencia_edit");
  const canDelete = usePermission("evidencia_delete");

  const isManzanillo = normalizePuerto(puertoEntrada) === "manzanillo";

  const [viewMode, setViewMode] = useState("general"); // "general" | "pedimento"
  const [filterSku, setFilterSku] = useState("all");
  const [filterClas, setFilterClas] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [clas, setClas] = useState("");
  const [skuVal, setSkuVal] = useState("_none");
  const [pedimentoVal, setPedimentoVal] = useState("_none");
  const [tipoEvVal, setTipoEvVal] = useState("_none");
  const [desc, setDesc] = useState("");
  const [previewEv, setPreviewEv] = useState(null);
  // Which pedimento the "Por Pedimento" button click is opening the dialog for
  const [pedimentoInputCtx, setPedimentoInputCtx] = useState(null); // { pedimento, tipo }

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["evidencias", otId],
    queryFn: async () => (await api.get(`/ots/${otId}/evidencias`)).data,
  });

  // Unique SKUs from the OT's line items (in entry order)
  const otSkus = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const it of otItems || []) {
      const s = (it.sku || "").trim();
      if (s && !seen.has(s)) {
        seen.add(s);
        out.push(s);
      }
    }
    return out;
  }, [otItems]);

  // Unique pedimentos from OT line items
  const otPedimentos = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const it of otItems || []) {
      const p = (it.pedimento || "").trim();
      if (p && !seen.has(p)) {
        seen.add(p);
        out.push(p);
      }
    }
    return out;
  }, [otItems]);

  const skuCounts = items.reduce((acc, e) => {
    const k = (e.sku || "").trim() || "_";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  const clasCounts = items.reduce((acc, e) => {
    const k = e.clasificacion || "_";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  // Group evidencias by pedimento (only for "Por Pedimento" view)
  const evidenciasByPedimento = useMemo(() => {
    const map = {};
    for (const ev of items) {
      const key = (ev.pedimento || "").trim() || "_sin_pedimento";
      if (!map[key]) map[key] = { llegada: [], contenedor_limpio: [], otras: [] };
      const tipo = ev.tipo_evidencia;
      if (tipo === "llegada") map[key].llegada.push(ev);
      else if (tipo === "contenedor_limpio") map[key].contenedor_limpio.push(ev);
      else map[key].otras.push(ev);
    }
    return map;
  }, [items]);

  const filtered = items.filter((e) => {
    if (filterSku !== "all") {
      const k = (e.sku || "").trim() || "_";
      if (k !== filterSku) return false;
    }
    if (filterClas !== "all") {
      if ((e.clasificacion || "_") !== filterClas) return false;
    }
    return true;
  });

  const resetDialogFields = () => {
    setClas("");
    setSkuVal(otSkus.length > 0 ? otSkus[0] : "_none");
    setPedimentoVal("_none");
    setTipoEvVal("_none");
    setDesc("");
    setPedimentoInputCtx(null);
  };

  const openDialogForGeneral = () => {
    resetDialogFields();
  };

  const openDialogForPedimento = (pedimento, tipo) => {
    // Prefill fields for pedimento-based upload
    setClas("");
    setSkuVal(otSkus.length > 0 ? otSkus[0] : "_none");
    setPedimentoVal(pedimento || "_none");
    setTipoEvVal(tipo || "_none");
    setDesc("");
    setPedimentoInputCtx({ pedimento, tipo });
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPendingFile(f);
    setDialogOpen(true);
    if (inputRef.current) inputRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
  };

  const triggerFilePicker = (kind, ctx) => {
    if (ctx) {
      openDialogForPedimento(ctx.pedimento, ctx.tipo);
    } else {
      openDialogForGeneral();
    }
    if (kind === "camera") cameraRef.current?.click();
    else inputRef.current?.click();
  };

  const confirmUpload = async () => {
    if (!pendingFile) return;
    const fd = new FormData();
    fd.append("file", pendingFile);
    if (clas) fd.append("clasificacion", clas);
    if (skuVal && skuVal !== "_none") fd.append("sku", skuVal);
    if (pedimentoVal && pedimentoVal !== "_none") fd.append("pedimento", pedimentoVal);
    if (tipoEvVal && tipoEvVal !== "_none") fd.append("tipo_evidencia", tipoEvVal);
    if (desc) fd.append("description", desc);
    setUploading(true);
    try {
      await api.post(`/ots/${otId}/evidencias`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Evidencia agregada");
      qc.invalidateQueries({ queryKey: ["evidencias", otId] });
      qc.invalidateQueries({ queryKey: ["history", otId] });
      setDialogOpen(false);
      setPendingFile(null);
      setPedimentoInputCtx(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir evidencia");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) {
      openDialogForGeneral();
      setPendingFile(f);
      setDialogOpen(true);
    }
  };

  const remove = async (ev) => {
    try {
      await api.delete(`/ots/${otId}/evidencias/${ev.id}`);
      toast.success("Evidencia eliminada");
      qc.invalidateQueries({ queryKey: ["evidencias", otId] });
      qc.invalidateQueries({ queryKey: ["history", otId] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    }
  };

  const download = async (ev) => {
    try {
      const res = await api.get(`/ots/${otId}/evidencias/${ev.id}/file`, {
        responseType: "blob",
      });
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = ev.filename || `evidencia-${ev.id}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success("Descarga iniciada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo descargar la imagen");
    }
  };

  const updateClas = async (ev, value) => {
    try {
      await api.patch(`/ots/${otId}/evidencias/${ev.id}`, { clasificacion: value === "_none" ? null : value });
      qc.invalidateQueries({ queryKey: ["evidencias", otId] });
    } catch (err) {
      toast.error("Error al actualizar");
    }
  };

  const updateSku = async (ev, value) => {
    try {
      await api.patch(`/ots/${otId}/evidencias/${ev.id}`, { sku: value === "_none" ? null : value });
      qc.invalidateQueries({ queryKey: ["evidencias", otId] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar");
    }
  };

  const renderCard = (ev, opts = { compact: false }) => (
    <Card key={ev.id} className="bg-card border-border overflow-hidden group" data-testid={`ev-card-${ev.id}`}>
      <div
        className="relative aspect-video bg-muted/40 cursor-zoom-in"
        onClick={() => setPreviewEv(ev)}
      >
        <img
          src={fileSrc(otId, ev.id)}
          alt={ev.filename}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {ev.sku && (
          <span className="absolute top-2 left-2 bg-card/90 backdrop-blur text-[10px] uppercase tracking-wider font-mono font-semibold px-2 py-0.5 rounded-md border border-border">
            <Package className="h-3 w-3 inline mr-1" />{ev.sku}
          </span>
        )}
        {ev.tipo_evidencia && (
          <span className={`absolute top-2 right-2 opacity-95 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-md border ${TIPO_EV_BADGE[ev.tipo_evidencia] || ""}`}>
            {TIPO_EV_LABEL[ev.tipo_evidencia]}
          </span>
        )}
        {ev.clasificacion && (
          <span className={`absolute bottom-2 left-2 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-md border ${CLAS_COLOR[ev.clasificacion] || ""}`}>
            Clas. {ev.clasificacion}
          </span>
        )}
        {canDelete && (
          <AlertDialog>
            <AlertDialogTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                size="icon"
                variant="destructive"
                className={`absolute ${ev.tipo_evidencia ? "top-9" : "top-2"} right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity`}
                data-testid={`ev-delete-${ev.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Eliminar esta evidencia?</AlertDialogTitle>
                <AlertDialogDescription>{ev.filename}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => remove(ev)}>Eliminar</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        <Button
          size="icon"
          variant="secondary"
          className={`absolute ${ev.tipo_evidencia ? "top-9" : "top-2"} ${canDelete ? "right-10" : "right-2"} h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity`}
          title="Descargar imagen"
          onClick={(e) => { e.stopPropagation(); download(ev); }}
          data-testid={`ev-download-${ev.id}`}
        >
          <Download className="h-3.5 w-3.5" />
        </Button>
      </div>
      <CardContent className="p-2.5 space-y-1.5">
        {!opts.compact && (
          <div className="grid grid-cols-2 gap-1">
            <Select value={ev.sku || "_none"} onValueChange={(v) => updateSku(ev, v)} disabled={!canEdit}>
              <SelectTrigger className="h-7 text-xs font-mono" data-testid={`ev-sku-select-${ev.id}`}>
                <SelectValue placeholder="Sin SKU" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Sin SKU</SelectItem>
                {otSkus.map((s) => (
                  <SelectItem key={s} value={s} className="font-mono">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={ev.clasificacion || "_none"} onValueChange={(v) => updateClas(ev, v)} disabled={!canEdit}>
              <SelectTrigger className="h-7 text-xs" data-testid={`ev-clas-select-${ev.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Sin clas.</SelectItem>
                <SelectItem value="B">Clas. B</SelectItem>
                <SelectItem value="C">Clas. C</SelectItem>
                <SelectItem value="D">Clas. D</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {ev.pedimento && (
          <div className="text-[10px] text-muted-foreground font-mono flex items-center gap-1">
            <FileText className="h-3 w-3" /> Pedimento: <span className="text-foreground/80">{ev.pedimento}</span>
          </div>
        )}
        {ev.description && (
          <p className="text-[11px] text-foreground/80 line-clamp-2" title={ev.description}>{ev.description}</p>
        )}
        <div className="text-[10px] text-muted-foreground flex items-center justify-between">
          <span className="truncate" title={ev.filename}>{ev.filename}</span>
          <span className="font-mono ml-2 shrink-0">{((ev.size || 0) / 1024).toFixed(0)} KB</span>
        </div>
        <div className="text-[10px] text-muted-foreground border-t border-border pt-1 mt-1">
          Por <span className="text-foreground/80">{ev.uploaded_by_name || ev.uploaded_by}</span> · {(ev.created_at || "").substring(0, 16).replace("T", " ")}
        </div>
      </CardContent>
    </Card>
  );

  const renderPedimentoAccordion = () => {
    if (otPedimentos.length === 0) {
      return (
        <Card className="bg-card border-dashed border-border">
          <CardContent className="p-6 text-center">
            <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <h3 className="text-sm font-medium">Esta OT no tiene pedimentos capturados</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Edita la OT y agrega el número de pedimento en las partidas para subir evidencias por pedimento.
            </p>
          </CardContent>
        </Card>
      );
    }

    return (
      <Accordion type="multiple" defaultValue={otPedimentos.slice(0, 1)} className="space-y-2">
        {otPedimentos.map((ped) => {
          const bucket = evidenciasByPedimento[ped] || { llegada: [], contenedor_limpio: [], otras: [] };
          const totalPed = bucket.llegada.length + bucket.contenedor_limpio.length + bucket.otras.length;
          const hasContenedorLimpio = bucket.contenedor_limpio.length > 0;

          return (
            <AccordionItem
              key={ped}
              value={ped}
              className="border border-border rounded-md bg-card/50 px-3"
              data-testid={`ev-pedimento-accordion-${ped}`}
            >
              <AccordionTrigger className="hover:no-underline py-3">
                <div className="flex items-center justify-between w-full pr-2 gap-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 text-primary shrink-0" />
                    <span className="font-mono text-sm text-foreground truncate">Pedimento {ped}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge variant="secondary" className="font-mono text-[10px]">
                      {totalPed} evidencia{totalPed !== 1 ? "s" : ""}
                    </Badge>
                    <Badge className={`text-[10px] border ${TIPO_EV_BADGE.llegada}`}>
                      Llegada: {bucket.llegada.length}
                    </Badge>
                    <Badge className={`text-[10px] border ${TIPO_EV_BADGE.contenedor_limpio}`}>
                      Limpio: {bucket.contenedor_limpio.length}
                    </Badge>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-4 pt-1 space-y-3">
                {/* Upload buttons per pedimento */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => triggerFilePicker("file", { pedimento: ped, tipo: "llegada" })}
                    disabled={!canUpload}
                    className="flex-1"
                    data-testid={`ev-ped-upload-llegada-${ped}`}
                  >
                    <ImageIcon className="h-4 w-4 mr-1.5" /> Subir foto de llegada
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => triggerFilePicker("camera", { pedimento: ped, tipo: "llegada" })}
                    disabled={!canUpload}
                    className="sm:w-auto"
                    data-testid={`ev-ped-camera-llegada-${ped}`}
                    title="Tomar foto con cámara"
                  >
                    <Camera className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => triggerFilePicker("file", { pedimento: ped, tipo: "contenedor_limpio" })}
                    disabled={!canUpload}
                    className="flex-1"
                    data-testid={`ev-ped-upload-limpio-${ped}`}
                  >
                    <PackageCheck className="h-4 w-4 mr-1.5" /> Subir foto de contenedor limpio
                  </Button>
                </div>

                {/* Manzanillo recommendation */}
                {isManzanillo && !hasContenedorLimpio && (
                  <div className="flex items-start gap-2 rounded-md border border-[hsl(38_92%_55%/0.4)] bg-[hsl(38_92%_55%/0.08)] p-2.5 text-xs">
                    <Info className="h-4 w-4 text-[hsl(var(--status-pending))] shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-[hsl(var(--status-pending))]">Recomendación · Puerto Manzanillo</div>
                      <p className="text-muted-foreground mt-0.5">
                        Se recomienda subir una foto del contenedor <b className="text-foreground/90">limpio</b> al terminar la descarga (opcional pero recomendada).
                      </p>
                    </div>
                  </div>
                )}

                {/* Llegada evidencias */}
                {bucket.llegada.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <ImageIcon className="h-3 w-3" /> Fotos de llegada · {bucket.llegada.length}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
                      {bucket.llegada.map((ev) => renderCard(ev))}
                    </div>
                  </div>
                )}

                {/* Contenedor limpio */}
                {bucket.contenedor_limpio.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <PackageCheck className="h-3 w-3" /> Fotos de contenedor limpio · {bucket.contenedor_limpio.length}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
                      {bucket.contenedor_limpio.map((ev) => renderCard(ev))}
                    </div>
                  </div>
                )}

                {/* Otras evidencias asociadas al pedimento (sin tipo) */}
                {bucket.otras.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <FileImage className="h-3 w-3" /> Otras evidencias · {bucket.otras.length}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
                      {bucket.otras.map((ev) => renderCard(ev))}
                    </div>
                  </div>
                )}

                {totalPed === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-3">
                    Sin evidencias para este pedimento todavía.
                  </p>
                )}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    );
  };

  return (
    <div className="space-y-3">
      {/* View mode toggle */}
      <div className="flex items-center justify-between flex-wrap gap-2 p-2 rounded-md border border-border bg-card/50">
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant={viewMode === "general" ? "default" : "ghost"}
            className="h-8 text-xs"
            onClick={() => setViewMode("general")}
            data-testid="ev-view-general"
          >
            <LayoutGrid className="h-3.5 w-3.5 mr-1.5" /> Vista general
          </Button>
          <Button
            size="sm"
            variant={viewMode === "pedimento" ? "default" : "ghost"}
            className="h-8 text-xs"
            onClick={() => setViewMode("pedimento")}
            data-testid="ev-view-pedimento"
          >
            <ListTree className="h-3.5 w-3.5 mr-1.5" /> Por pedimento
            {otPedimentos.length > 0 && (
              <Badge variant="secondary" className="ml-2 font-mono text-[10px]">{otPedimentos.length}</Badge>
            )}
          </Button>
        </div>
        <div className="text-[10px] text-muted-foreground">
          {isManzanillo ? (
            <span className="inline-flex items-center gap-1">
              <Info className="h-3 w-3 text-[hsl(var(--status-pending))]" /> Puerto Manzanillo detectado
            </span>
          ) : (
            <span>Puerto: {puertoEntrada || "—"}</span>
          )}
        </div>
      </div>

      {/* Hidden inputs (shared between general and pedimento views) */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onPick}
        data-testid="ev-upload-input"
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onPick}
        data-testid="ev-camera-input"
      />

      {/* GENERAL VIEW (existing behavior) */}
      {viewMode === "general" && (
        <>
          {/* SKU filter row */}
          <div className="flex items-center gap-2 flex-wrap p-2 rounded-md border border-border bg-card/50">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1 flex items-center gap-1">
              <Package className="h-3.5 w-3.5" /> SKU:
            </div>
            <Button
              size="sm"
              variant={filterSku === "all" ? "default" : "ghost"}
              className="h-7 text-xs"
              onClick={() => setFilterSku("all")}
              data-testid="ev-sku-filter-all"
            >
              Todos <Badge variant="secondary" className="ml-2 font-mono">{items.length}</Badge>
            </Button>
            {otSkus.map((s) => (
              <Button
                key={s}
                size="sm"
                variant={filterSku === s ? "default" : "ghost"}
                className="h-7 text-xs font-mono"
                onClick={() => setFilterSku(s)}
                data-testid={`ev-sku-filter-${s}`}
              >
                {s} <Badge variant="secondary" className="ml-2 font-mono">{skuCounts[s] || 0}</Badge>
              </Button>
            ))}
            {(skuCounts["_"] || 0) > 0 && (
              <Button
                size="sm"
                variant={filterSku === "_" ? "default" : "ghost"}
                className="h-7 text-xs"
                onClick={() => setFilterSku("_")}
              >
                Sin SKU <Badge variant="secondary" className="ml-2 font-mono">{skuCounts["_"]}</Badge>
              </Button>
            )}
          </div>

          {/* Toolbar */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Clas:</span>
              <Button
                size="sm"
                variant={filterClas === "all" ? "default" : "ghost"}
                className="h-7 text-xs"
                onClick={() => setFilterClas("all")}
                data-testid="ev-filter-all"
              >
                Todas
              </Button>
              {["B", "C", "D"].map((c) => (
                <Button
                  key={c}
                  size="sm"
                  variant={filterClas === c ? "default" : "ghost"}
                  className="h-7 text-xs"
                  onClick={() => setFilterClas(c)}
                  data-testid={`ev-filter-${c}`}
                >
                  Clas. {c} <Badge variant="secondary" className="ml-2 font-mono">{clasCounts[c] || 0}</Badge>
                </Button>
              ))}
              <Button
                size="sm"
                variant={filterClas === "_" ? "default" : "ghost"}
                className="h-7 text-xs"
                onClick={() => setFilterClas("_")}
              >
                General <Badge variant="secondary" className="ml-2 font-mono">{clasCounts["_"] || 0}</Badge>
              </Button>
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => triggerFilePicker("camera", null)}
                data-testid="ev-camera-button"
                disabled={!canUpload}
                title={canUpload ? "Tomar foto con la cámara" : "Sin permiso 'evidencia_upload'"}
              >
                <Camera className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Tomar foto</span>
              </Button>
              <Button
                size="sm"
                onClick={() => triggerFilePicker("file", null)}
                data-testid="ev-upload-button"
                disabled={!canUpload}
                title={canUpload ? "Subir imagen desde el dispositivo" : "Sin permiso 'evidencia_upload'"}
              >
                <Plus className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Elegir archivo</span>
              </Button>
            </div>
          </div>

          {items.length === 0 && !isLoading && (
            <Card
              className={`bg-card border-dashed border-border transition-colors ${canUpload ? "hover:border-primary/40" : "opacity-70"}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={canUpload ? onDrop : undefined}
              data-testid="ev-empty-dropzone"
            >
              <CardContent className="p-8 flex flex-col items-center text-center">
                <FileImage className="h-12 w-12 text-muted-foreground mb-3" />
                <h3 className="text-sm font-medium">Sin evidencias todavía</h3>
                <p className="text-xs text-muted-foreground mt-1 mb-4">
                  {canUpload ? (
                    <>En dispositivos móviles puedes <b className="text-foreground">tomar una foto</b> con la cámara o <b className="text-foreground">elegir una imagen</b> de tu galería/archivos.</>
                  ) : (
                    <>Necesitas el permiso <span className="font-mono text-foreground">evidencia_upload</span> para subir imágenes.</>
                  )}
                </p>
                {canUpload && (
                  <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => triggerFilePicker("camera", null)}
                      data-testid="ev-empty-camera-button"
                    >
                      <Camera className="h-4 w-4 mr-1.5" /> Tomar foto
                    </Button>
                    <Button size="sm" onClick={() => triggerFilePicker("file", null)} data-testid="ev-empty-upload-button">
                      <Upload className="h-4 w-4 mr-1.5" /> Elegir archivo
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {items.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
              {filtered.map((ev) => renderCard(ev))}
              {filtered.length === 0 && (
                <div className="col-span-full text-center text-sm text-muted-foreground py-10">
                  No hay evidencias con los filtros seleccionados.
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* PEDIMENTO VIEW */}
      {viewMode === "pedimento" && renderPedimentoAccordion()}

      {/* Lightbox */}
      {previewEv && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewEv(null)}
        >
          <div className="absolute top-4 right-4 flex gap-2" onClick={(e) => e.stopPropagation()}>
            <Button
              size="icon"
              variant="secondary"
              onClick={() => download(previewEv)}
              title="Descargar imagen"
              data-testid="ev-lightbox-download"
            >
              <Download className="h-5 w-5" />
            </Button>
            <Button
              size="icon"
              variant="secondary"
              onClick={() => setPreviewEv(null)}
              title="Cerrar"
              data-testid="ev-lightbox-close"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <img
            src={fileSrc(otId, previewEv.id)}
            alt={previewEv.filename || "Vista"}
            className="max-w-full max-h-full object-contain rounded-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Upload dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) {
            setPendingFile(null);
            setPedimentoInputCtx(null);
          }
        }}
      >
        <DialogContent className="max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Subir evidencia · OT {folio}
              {pedimentoInputCtx?.pedimento && (
                <span className="block text-xs font-normal text-muted-foreground mt-1">
                  Pedimento <span className="font-mono text-foreground/80">{pedimentoInputCtx.pedimento}</span>
                  {pedimentoInputCtx.tipo && (
                    <> · Tipo: <span className="text-foreground/80">{TIPO_EV_LABEL[pedimentoInputCtx.tipo]}</span></>
                  )}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-border p-3 bg-muted/30 text-xs">
              <div className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-primary" />
                <span className="font-mono truncate">{pendingFile?.name}</span>
                <span className="text-muted-foreground ml-auto">{((pendingFile?.size || 0) / 1024).toFixed(0)} KB</span>
              </div>
            </div>

            {/* Pedimento */}
            {otPedimentos.length > 0 && (
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Pedimento (opcional)</Label>
                <Select value={pedimentoVal} onValueChange={setPedimentoVal}>
                  <SelectTrigger className="mt-1" data-testid="ev-dialog-pedimento-select">
                    <SelectValue placeholder="Sin pedimento específico" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Sin pedimento específico</SelectItem>
                    {otPedimentos.map((p) => (
                      <SelectItem key={p} value={p} className="font-mono">{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Tipo de evidencia */}
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Tipo de evidencia (opcional)</Label>
              <Select value={tipoEvVal} onValueChange={setTipoEvVal}>
                <SelectTrigger className="mt-1" data-testid="ev-dialog-tipo-select">
                  <SelectValue placeholder="Sin tipo específico" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Sin tipo específico</SelectItem>
                  <SelectItem value="llegada">Llegada (cómo llega el contenedor)</SelectItem>
                  <SelectItem value="contenedor_limpio">Contenedor limpio (fin de descarga)</SelectItem>
                </SelectContent>
              </Select>
              {isManzanillo && tipoEvVal !== "contenedor_limpio" && pedimentoInputCtx?.tipo !== "contenedor_limpio" && (
                <p className="text-[11px] text-[hsl(var(--status-pending))] mt-1 flex items-start gap-1">
                  <Info className="h-3 w-3 mt-0.5 shrink-0" />
                  Manzanillo: se recomienda subir también la foto de contenedor limpio.
                </p>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">SKU de la OT (opcional)</Label>
              <Select value={skuVal} onValueChange={setSkuVal}>
                <SelectTrigger className="mt-1" data-testid="ev-dialog-sku-select">
                  <SelectValue placeholder="Selecciona un SKU" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Sin SKU específico (general)</SelectItem>
                  {otSkus.map((s) => (
                    <SelectItem key={s} value={s} className="font-mono">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {otSkus.length === 0 && (
                <p className="text-[11px] text-amber-400 mt-1">
                  Esta OT no tiene SKUs definidos. Edita la OT y agrega SKUs en las partidas para asociarlos.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Clasificación (opcional)</Label>
              <Select value={clas} onValueChange={setClas}>
                <SelectTrigger className="mt-1" data-testid="ev-dialog-clas-select">
                  <SelectValue placeholder="Sin clasificación específica" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="B">Clasificación B</SelectItem>
                  <SelectItem value="C">Clasificación C</SelectItem>
                  <SelectItem value="D">Clasificación D</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Descripción (opcional)</Label>
              <Textarea
                rows={2}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                placeholder="Detalle de la evidencia, condición observada, etc."
                data-testid="ev-dialog-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={confirmUpload} disabled={uploading} data-testid="ev-dialog-submit">
              {uploading ? "Subiendo..." : "Guardar evidencia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
